// src/pages/ConversationPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronDown, UserCircle, Megaphone, Users } from 'lucide-react';
import conversationService from '../services/conversationService';
import userService from '../services/userService';
import notificationService from '../services/notificationService';
import { ChatBubble, ChatInput } from '../components/chat';
import { Loading, ErrorState } from '../components/ui';
import ProfileModal from '../modals/ProfileModal';

const ConversationPage = ({ user, toast, sidebarCollapsed }) => {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [otherReadAt, setOtherReadAt] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessagesLen = useRef(0);
  const isAtBottomRef = useRef(true);

  const formatDateSeparator = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return t('conversation.dateSeparator.today');
    if (date.toDateString() === yesterday.toDateString())
      return t('conversation.dateSeparator.yesterday');
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  // Mount : valider la conversation via getDoc
  useEffect(() => {
    if (!user?.uid || !conversationId) {
      navigate('/messages');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const conv = await conversationService.getConversation(conversationId);
        if (cancelled) return;

        if (!conv || !conv.participants.includes(user.uid)) {
          navigate('/messages');
          return;
        }

        setConversation(conv);
        const otherUid = conv.participants.find((p) => p !== user.uid);
        const profile = await userService.getProfile(otherUid);
        if (cancelled) return;

        setOtherUser(profile);
        await conversationService.markAsRead(conversationId, user.uid);
        notificationService.markAsRead(`msg_${conversationId}_${user.uid}`).catch(() => {});
      } catch (err) {
        console.error('ConversationPage init error:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [conversationId, user?.uid, navigate]);

  // Abonnement temps réel aux messages (après validation)
  useEffect(() => {
    if (loading || !user?.uid || !conversationId) return;

    const unsub = conversationService.subscribeToMessages(conversationId, (msgs) => {
      setMessages(msgs);

      // Marquer comme lu si nouveau message de l'autre
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.senderId !== user.uid) {
        conversationService.markAsRead(conversationId, user.uid);
        notificationService.markAsRead(`msg_${conversationId}_${user.uid}`).catch(() => {});
        setTimeout(() => {
          notificationService.markAsRead(`msg_${conversationId}_${user.uid}`).catch(() => {});
        }, 3000);
      }
    });

    return unsub;
  }, [loading, conversationId, user?.uid]);

  // Abonnement temps réel au document conversation (readBy pour read receipts)
  useEffect(() => {
    if (loading || !otherUser?.uid || !conversationId) return;

    const unsub = conversationService.subscribeToConversation(conversationId, (conv) => {
      setOtherReadAt(conv.readBy?.[otherUser.uid] || null);
    });

    return unsub;
  }, [loading, conversationId, otherUser?.uid]);

  // Scroll position tracking
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    isAtBottomRef.current = distanceFromBottom < 100;
    setShowScrollBtn(distanceFromBottom > 200);
  };

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Auto-scroll
  useEffect(() => {
    if (messages.length === 0) return;

    // Premier chargement : scroll instantané vers le bas
    if (prevMessagesLen.current === 0) {
      setTimeout(() => scrollToBottom('instant'), 0);
      prevMessagesLen.current = messages.length;
      return;
    }

    // Nouveau message : scroll si l'utilisateur est déjà en bas
    if (messages.length > prevMessagesLen.current && isAtBottomRef.current) {
      scrollToBottom();
    }

    prevMessagesLen.current = messages.length;
  }, [messages]);

  const handleSend = async (text) => {
    setSending(true);
    scrollToBottom();
    const result = await conversationService.sendMessage(conversationId, user.uid, text);
    if (!result.success && toast) {
      toast(t('conversation.toast.sendError'), 'error');
    }
    setSending(false);
  };

  const handleSendPhoto = async (file, caption = '') => {
    setSending(true);
    const upload = await conversationService.uploadChatImage(user.uid, file);
    if (upload.success) {
      scrollToBottom();
      const result = await conversationService.sendMessage(
        conversationId,
        user.uid,
        caption,
        upload.url
      );
      if (!result.success && toast) {
        toast(t('conversation.toast.photoError'), 'error');
      }
    } else {
      if (toast) toast(t('conversation.toast.imageError'), 'error');
    }
    setSending(false);
  };

  if (!user) return null;

  if (loading) {
    return (
      <div
        className={`fixed inset-0 flex flex-col items-center justify-center pt-16 z-20 bg-white dark:bg-gray-950 conversation-page ${user ? (sidebarCollapsed ? 'lg:left-16' : 'lg:left-64') : ''}`}
      >
        <Loading msg={t('common.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`fixed inset-0 flex flex-col items-center justify-center pt-16 z-20 bg-white dark:bg-gray-950 conversation-page ${user ? (sidebarCollapsed ? 'lg:left-16' : 'lg:left-64') : ''}`}
      >
        <ErrorState onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 flex flex-col pt-16 z-20 bg-white dark:bg-gray-950 conversation-page ${user ? (sidebarCollapsed ? 'lg:left-16' : 'lg:left-64') : ''}`}
    >
      <Helmet>
        <title>{t('conversation.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/messages" />
      </Helmet>

      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl">
        <button
          data-testid="chat-btn-back"
          onClick={() => navigate('/messages')}
          aria-label={t('conversation.backToMessages')}
          className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <button
          className="flex items-center gap-3 flex-1 min-w-0"
          onClick={() => setShowProfile(true)}
          aria-label={t('conversation.viewProfile', {
            name: otherUser?.name || t('conversation.defaultUser'),
          })}
        >
          {otherUser?.profilePicture ? (
            <img
              src={otherUser.profilePicture}
              alt={otherUser.name || ''}
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <UserCircle
              size={40}
              className="text-gray-300 dark:text-gray-600 flex-shrink-0 sm:w-11 sm:h-11"
            />
          )}
          <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
            {otherUser?.name || t('conversation.defaultUser')}
          </span>
        </button>
      </div>

      {/* Bandeau ami */}
      {conversation?.type === 'friend' && (
        <div
          data-testid="conversation-friend-banner"
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 sm:py-2.5 bg-violet-50 dark:bg-violet-950/30 border-b border-violet-100 dark:border-violet-900/50"
        >
          <Users size={16} className="text-violet-500 dark:text-violet-400 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-medium text-violet-600 dark:text-violet-400">
            {t('messages.friendBanner')}
          </span>
        </div>
      )}

      {/* Bandeau annonce */}
      {conversation?.type === 'annonce' && conversation.annonceTitle && (
        <button
          data-testid="conversation-annonce-banner"
          onClick={() => navigate(`/annonces/${conversation.annonceId}`)}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 sm:py-2.5 bg-indigo-50 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-900/50 hover:bg-indigo-100 dark:hover:bg-indigo-950/50 transition-colors"
        >
          <Megaphone size={16} className="text-indigo-500 dark:text-indigo-400 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-medium text-indigo-600 dark:text-indigo-400 truncate">
            {conversation.annonceTitle}
          </span>
        </button>
      )}

      {/* Zone de messages + bouton scroll */}
      <div className="flex-1 min-h-0 relative">
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="absolute inset-0 overflow-y-auto px-4 py-3 space-y-1"
        >
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400 dark:text-gray-500 text-sm">
                {t('conversation.emptyChat')}
              </p>
            </div>
          ) : (
            messages.map((msg, i) => {
              const prevMsg = i > 0 ? messages[i - 1] : null;
              const isMine = msg.senderId === user.uid;

              // Séparateur de date
              const showDateSep = (() => {
                if (!prevMsg) return true;
                const curr = msg.createdAt?.toDate?.();
                const prev = prevMsg.createdAt?.toDate?.();
                if (!curr || !prev) return false;
                return curr.toDateString() !== prev.toDateString();
              })();

              // Timestamp : affiché si > 5 min d'écart avec le message précédent
              const showTimestamp = (() => {
                if (!prevMsg) return true;
                const curr = msg.createdAt?.toMillis?.() || 0;
                const prev = prevMsg.createdAt?.toMillis?.() || 0;
                return curr - prev > 5 * 60 * 1000;
              })();

              return (
                <React.Fragment key={msg.id}>
                  {showDateSep && (
                    <div className="flex justify-center my-4">
                      <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                        {formatDateSeparator(msg.createdAt)}
                      </span>
                    </div>
                  )}
                  <ChatBubble
                    message={msg}
                    isMine={isMine}
                    showTimestamp={showTimestamp}
                    otherReadAt={otherReadAt}
                  />
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Bouton revenir en bas */}
        {showScrollBtn && (
          <button
            onClick={() => scrollToBottom()}
            aria-label={t('conversation.scrollDown')}
            className="absolute bottom-4 right-4 z-10 p-2.5 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all animate-fade-in-up"
          >
            <ChevronDown size={20} />
          </button>
        )}
      </div>

      {/* Saisie */}
      <div className="flex-shrink-0">
        <ChatInput onSend={handleSend} onSendPhoto={handleSendPhoto} disabled={sending} />
      </div>

      {/* Modal profil */}
      <ProfileModal
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        profile={otherUser}
        onLike={() => {}}
        hasLiked={(user.likes || []).includes(otherUser?.uid)}
        toast={toast}
        currentUser={user}
        onBlock={() => {
          setShowProfile(false);
          navigate('/messages');
        }}
      />
    </div>
  );
};

export default ConversationPage;
